<template>
  <!-- Kit PhoneNav (the phone boards' nav): a 44 px row with "‹ Sessions"
       (or the left slot), the inline title once the large title has
       scrolled away, and the right slot, which ends with the upload
       queue's ring (SI-19). Then the 34/700 large title, and the below
       slot (search, segmented, chips). The large title drops when the
       window is under 700 px tall. -->
  <header class="phone-nav" :class="{ 'is-collapsed': collapsed || !showLarge, 'has-back': !!back && !$slots.left }">
    <div class="phone-nav-row">
      <div class="phone-nav-left">
        <slot name="left">
          <button v-if="back" type="button" class="phone-nav-back" @click="goBack">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
            <span>{{ back }}</span>
          </button>
        </slot>
      </div>
      <div class="phone-nav-inline" :aria-hidden="showLarge && !collapsed ? 'true' : 'false'">{{ title }}</div>
      <div class="phone-nav-right">
        <slot name="right" />
        <UploadQueueButton v-if="uploads" />
      </div>
    </div>
    <h1 v-if="large && showLarge && title" class="phone-nav-large">{{ title }}</h1>
    <div v-if="$slots.below" class="phone-nav-below"><slot name="below" /></div>
  </header>
</template>

<script>
import UploadQueueButton from "@/shell/UploadQueueButton.vue";
import { viewport } from "@/shell/sizeClass.js";
import { lastSessionListPath } from "@/shell/shellState.js";

export default {
  name: "PhoneNav",
  components: { UploadQueueButton },
  props: {
    // The back label ("Sessions"); none hides the back button.
    back: { type: String, default: "" },
    // Where back goes; the last session list when not given.
    backTo: { type: [String, Object], default: null },
    title: { type: String, default: "" },
    large: { type: Boolean, default: false },
    uploads: { type: Boolean, default: true },
  },
  emits: ["back"],
  data: () => ({ collapsed: false }),
  computed: {
    showLarge() {
      return this.large && viewport.height >= 700;
    },
  },
  mounted() {
    this.onScroll = () => {
      this.collapsed = window.scrollY > 40;
    };
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.onScroll();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    goBack() {
      this.$emit("back");
      this.$router.push(this.backTo || lastSessionListPath()).catch(() => {});
    },
  },
};
</script>

<style scoped>
/* The row sticks to the top while the large title scrolls away under it. */
.phone-nav {
  display: contents;
}
.phone-nav-row {
  position: sticky;
  top: 0;
  z-index: 5;
  background: var(--cl-bg);
  height: 44px;
  display: flex;
  align-items: center;
  padding: 0 16px;
}
/* With "‹ Back" the boards pad the row 4 on the left (the 24 px chevron
   carries its own inset) and 8 on the right (44 px targets). */
.has-back .phone-nav-row {
  padding: 0 8px 0 4px;
}
.phone-nav-left,
.phone-nav-right {
  display: flex;
  align-items: center;
  min-width: 0;
  z-index: 1;
}
.phone-nav-right {
  margin-left: auto;
  gap: 2px;
}
.phone-nav-back {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 44px;
  padding: 0;
  border: none;
  background: none;
  color: var(--cl-accent-text);
  font: 400 17px/22px var(--cl-font);
  cursor: pointer;
}
.phone-nav-inline {
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  font: 600 17px/22px var(--cl-font);
  color: var(--cl-label);
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--cl-t-fast);
}
.is-collapsed .phone-nav-inline {
  opacity: 1;
}
.phone-nav-large,
.phone-nav-below {
  background: var(--cl-bg);
}
.phone-nav-large {
  margin: 0;
  padding: 2px 16px 0;
  font: 700 34px/41px var(--cl-font-display);
  letter-spacing: 0.3px;
  color: var(--cl-label);
}
</style>
